import type { Avatars } from "./Avatars";
import { MAP_SIZE } from "../map/Iso";

const WALK_SPEED = 0.004; // world tiles per ms — matches server's 4 tiles/sec
const RETARGET_MARGIN = 0.2; // retarget when this close to destination

interface Bot {
  id: string;
  x: number;
  y: number;
  tx: number;
  ty: number;
  dir: number;
}

export class Bots {
  private readonly bots: Bot[] = [];
  private readonly avatars: Avatars;

  constructor(count: number, avatars: Avatars) {
    this.avatars = avatars;
    for (let i = 0; i < count; i++) {
      const x = Math.random() * (MAP_SIZE - 2) + 1;
      const y = Math.random() * (MAP_SIZE - 2) + 1;
      const bot: Bot = { id: `bot-${i}`, x, y, tx: x, ty: y, dir: 0 };
      this.bots.push(bot);
      avatars.upsert(bot.id, { x, y, dir: 0 });
    }
  }

  tick(dtMs: number): void {
    for (const bot of this.bots) {
      const dx = bot.tx - bot.x;
      const dy = bot.ty - bot.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < RETARGET_MARGIN) {
        bot.tx = Math.random() * (MAP_SIZE - 2) + 1;
        bot.ty = Math.random() * (MAP_SIZE - 2) + 1;
        continue;
      }

      const step = Math.min(WALK_SPEED * dtMs, dist);
      bot.x += (dx / dist) * step;
      bot.y += (dy / dist) * step;

      const angle = Math.atan2(dy, dx);
      bot.dir = Math.round(((angle / (Math.PI / 4)) + 8) % 8);

      this.avatars.upsert(bot.id, { x: bot.x, y: bot.y, dir: bot.dir });
    }
  }

  get count(): number {
    return this.bots.length;
  }

  clear(): void {
    for (const bot of this.bots) {
      this.avatars.remove(bot.id);
    }
    this.bots.length = 0;
  }
}

export function parseBotCount(): number {
  if (typeof window === "undefined") return 0;
  const raw = new URLSearchParams(window.location.search).get("bots");
  if (!raw) return 0;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? Math.min(Math.max(n, 0), 500) : 0;
}
